import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Sun, Moon, LogOut, Home, Sparkles, Plane, Share2, Shield, User, Trophy, Zap, HelpCircle, Search, Briefcase, Crown, Eye, EyeOff, Receipt, Palette } from 'lucide-react';
import { useImpersonate, type ViewAsRole } from '../../context/ImpersonateContext';
import { useSearch } from '../search/GlobalSearch';
import { useSubscription } from '../../context/SubscriptionContext';
import { isAgentPlan, PLAN_CONFIG } from '../../lib/subscriptions';
import { getRecentErrorCount } from '../../lib/error-logger';
import { isBetaMode } from '../../lib/beta-gate';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useCosmetics } from '../../context/CosmeticsContext';
import { useExperience } from '../../context/ExperienceContext';
import { useDesignSystemV2 } from '../../context/DesignSystemV2Context';
import { useTravellerType } from '../../hooks/useTravellerType';
import { useGamification } from '../../hooks/useGamification';
import { Button } from '../ui/Button';
import { LanguageSelector } from '../ui/LanguageSelector';
import { NotificationCenter } from '../notifications/NotificationCenter';
import { CreditsBanner } from '../credits/CreditsBanner';
import { UserAvatar } from '../ui/UserAvatar';
import { TitleBadge } from '../ui/TitleBadge';
import { ensureUserCredits } from '../../lib/credits';
import { supabase } from '../../lib/supabase';
import { TAX_FREE_SYSTEM_CHANGE_DATE } from '../../lib/tax-free';
import { formatNumber } from '../../lib/format-number';
import { useDailyCheckIn } from '../../context/DailyCheckInContext';
import { useTranslation } from 'react-i18next';

const PUBLIC_NAV_ITEMS = [
  { to: '/', labelKey: 'nav.home', subtitleKey: 'nav.homeGuestSubtitle', icon: Home },
];

const AUTH_NAV_ITEMS = [
  { to: '/', labelKey: 'nav.home', subtitleKey: 'nav.homeAuthSubtitle', icon: Home },
  { to: '/dream', labelKey: 'nav.dream', subtitleKey: 'nav.dreamSubtitle', icon: Sparkles },
  { to: '/travel', labelKey: 'nav.travel', subtitleKey: 'nav.travelSubtitle', icon: Plane },
  { to: '/share', labelKey: 'nav.share', subtitleKey: 'nav.shareSubtitle', icon: Share2 },
];

export function Navbar() {
  const { user, signOut, isAdmin } = useAuth();
  const betaGuestMode = isBetaMode() && !user;
  const { viewAsRole, isImpersonating, setViewAsRole, resetToAdmin, effectiveIsAdmin, effectivePlanSlug } = useImpersonate();
  const { theme, toggleTheme } = useTheme();
  const { activeFrame, activeTitle } = useCosmetics();
  const { isManga, isUkiyo, isMangaV3, isMangaBW, toggleMangaBW } = useExperience();
  const { activeV2Theme, isV2Active } = useDesignSystemV2();
  const isV2Standard = isV2Active && activeV2Theme === 'new-standard';
  const isV2Manga = (isV2Active && activeV2Theme === 'new-manga') || isMangaV3;
  const isV2Ukiyo = isV2Active && activeV2Theme === 'new-ukiyo';
  const { isCasual } = useTravellerType();
  const { enabled: gamificationEnabled, isAgent } = useGamification();
  const { hasCheckedInToday, showCheckInIfNeeded } = useDailyCheckIn();
  const { subscription } = useSubscription();
  const search = useSearch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [adminErrorCount, setAdminErrorCount] = useState(0);

  const toggleTravelMode = useCallback(async () => {
    if (!user) return;
    const newType = isCasual ? 'enthusiast' : 'casual';
    try {
      await supabase.from('user_preferences').upsert({
        user_id: user.id,
        traveller_type: newType,
        gamification_unlocked: newType === 'enthusiast',
      }, { onConflict: 'user_id' });
      window.location.reload();
    } catch (e) {
      console.error('Failed to toggle travel mode:', e);
    }
  }, [user, isCasual]);

  // Chromeless routes: minimal flows that intentionally show no navbar/footer.
  // Normalize pathname (strip trailing slash, lowercase) so /Beta-SignIn/ also matches.
  const chromelessRoutes = ['/beta-signin', '/beta-signup', '/unsubscribe'];
  const normalizedPath = location.pathname.replace(/\/$/, '').toLowerCase() || '/';
  const isChromeless = chromelessRoutes.includes(normalizedPath);

  useEffect(() => {
    if (!effectiveIsAdmin) return;
    getRecentErrorCount().then(setAdminErrorCount).catch(() => {});
    const interval = setInterval(() => getRecentErrorCount().then(setAdminErrorCount).catch(() => {}), 300000);

    // Listen for the "errors seen" event from ErrorLogsTab
    const onSeen = () => setAdminErrorCount(0);
    window.addEventListener('tg-errors-seen', onSeen);
    return () => { clearInterval(interval); window.removeEventListener('tg-errors-seen', onSeen); };
  }, [effectiveIsAdmin]);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [showTaxFreeNav, setShowTaxFreeNav] = useState(false);

  useEffect(() => {
    if (!user) return;
    ensureUserCredits(user.id).then(c => setCreditBalance(c.balance)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) { setShowTaxFreeNav(false); return; }
    if (new Date() >= TAX_FREE_SYSTEM_CHANGE_DATE) { setShowTaxFreeNav(true); return; }
    supabase
      .from('trips')
      .select('end_date')
      .eq('user_id', user.id)
      .gte('end_date', '2026-11-01')
      .limit(1)
      .then(({ data }) => setShowTaxFreeNav(!!(data && data.length > 0)));
  }, [user]);

  const allNavItems = useMemo(() => {
    const items = user ? [...AUTH_NAV_ITEMS] : [...PUBLIC_NAV_ITEMS];
    if (user && effectiveIsAdmin) {
      items.push({ to: '/admin', labelKey: 'nav.admin', subtitleKey: 'nav.adminSubtitle', icon: Shield });
    }
    if (user && isAgentPlan(subscription.planSlug)) {
      items.push({ to: '/agent', labelKey: 'nav.agent', subtitleKey: 'nav.agentSubtitle', icon: Briefcase });
    }
    return items;
  }, [user, effectiveIsAdmin, subscription.planSlug]);

  const navTourId = (to: string): string | undefined => {
    if (to === '/dream') return 'nav-dream';
    if (to === '/travel') return 'nav-travel';
    if (to === '/share') return 'nav-share';
    return undefined;
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/' || location.pathname === '/dashboard';
    if (path === '/dream') {
      return location.pathname.startsWith('/dream') || location.pathname.startsWith('/discover') || location.pathname.startsWith('/locations') || location.pathname === '/passport';
    }
    if (path === '/travel') {
      return location.pathname.startsWith('/travel') || location.pathname.startsWith('/my-trips') || location.pathname.startsWith('/trip-planner');
    }
    if (path === '/share') {
      return location.pathname.startsWith('/share') || location.pathname === '/community';
    }
    return location.pathname.startsWith(path);
  };

  /* ── User menu dropdown — shared across all three navbar modes ── */
  const userMenuDropdown = showUserMenu && (
    <div
      className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-warm-lg border border-gray-200 dark:border-gray-700 py-2 animate-slide-down overflow-y-auto overscroll-contain"
      style={{ maxHeight: 'calc(100dvh - 80px)' }}
    >
      <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.email}</p>
      </div>
      <Link to="/profile" className="flex items-center space-x-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => setShowUserMenu(false)}>
        <User size={16} /><span>{t('nav.profile')}</span>
      </Link>
      {user && !isAgent && (
        <div className="px-4 py-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5 flex items-center gap-1">
            <Sparkles size={11} /> {t('nav.travelMode')}
          </p>
          <div role="radiogroup" aria-label={t('nav.travelMode')} className="flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-xs font-semibold">
            <button
              role="radio"
              aria-checked={isCasual}
              onClick={() => { if (!isCasual) toggleTravelMode(); }}
              className={`flex-1 px-3 py-1.5 rounded-md transition-all ${
                isCasual
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {t('nav.casual')}
            </button>
            <button
              role="radio"
              aria-checked={!isCasual}
              onClick={() => { if (isCasual) toggleTravelMode(); }}
              className={`flex-1 px-3 py-1.5 rounded-md transition-all ${
                !isCasual
                  ? 'bg-white dark:bg-gray-900 text-primary-600 dark:text-primary-400 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {t('nav.enthusiast')}
            </button>
          </div>
        </div>
      )}
      {gamificationEnabled && (
        <>
          <Link to="/achievements" className="flex items-center space-x-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => setShowUserMenu(false)}>
            <Trophy size={16} /><span>{t('nav.achievements')}</span>
          </Link>
          <Link to="/credits" className="flex items-center space-x-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => setShowUserMenu(false)}>
            <Zap size={16} /><span>{t('nav.credits')}</span>
          </Link>
        </>
      )}
      <Link to="/tax-free/tracker" className="flex items-center space-x-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => setShowUserMenu(false)}>
        <Receipt size={16} /><span>{t('nav.taxFree')}</span>
        <span
          className="ml-auto px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded"
          style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
        >
          {t('common:common.new')}
        </span>
      </Link>
      <Link to="/subscription" className="flex items-center space-x-2 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => setShowUserMenu(false)}>
        <Crown size={16} /><span>{t('nav.myPlan')}</span>
      </Link>
      {isAdmin && (
        <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-1">
          {!isImpersonating ? (
            <div className="px-4 py-2">
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                {t('impersonate.title')}
              </p>
              <div className="space-y-1">
                {([
                  { role: 'free' as ViewAsRole, label: t('impersonate.freeUser') },
                  { role: 'explorer' as ViewAsRole, label: t('impersonate.explorer') },
                  { role: 'sensei' as ViewAsRole, label: t('impersonate.sensei') },
                  { role: 'concierge' as ViewAsRole, label: t('impersonate.concierge') },
                  { role: 'agent-50' as ViewAsRole, label: t('impersonate.agent50') },
                ]).map(({ role, label }) => (
                  <button
                    key={role}
                    onClick={() => { setViewAsRole(role); setShowUserMenu(false); }}
                    className="flex items-center gap-2 w-full px-2 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Eye size={12} />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button
              onClick={() => { resetToAdmin(); setShowUserMenu(false); }}
              className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors font-medium"
            >
              <EyeOff size={16} />
              <span>{t('impersonate.returnToAdmin')}</span>
            </button>
          )}
        </div>
      )}
      <div className="border-t border-gray-100 dark:border-gray-700 mt-1 pt-1">
        <button onClick={() => { setShowUserMenu(false); signOut(); }} className="flex items-center space-x-2 px-4 py-2.5 text-sm text-error-600 dark:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 w-full text-left transition-colors">
          <LogOut size={16} /><span>{t('nav.signOut')}</span>
        </button>
      </div>
    </div>
  );

  /* ── Admin impersonation banner — fixed at very top ── */
  const impersonationBanner = isImpersonating && (() => {
    const planConfig = PLAN_CONFIG[effectivePlanSlug] || PLAN_CONFIG.free;
    const hasExport = planConfig.features.includes('feature-export');
    const hasAnalytics = planConfig.features.includes('feature-analytics');
    const hasCanvas = planConfig.features.includes('feature-canvas');
    const hasNoAds = planConfig.features.includes('feature-no-ads');
    const hasAgent = planConfig.features.includes('feature-agent');
    const aiSuggestions = planConfig.aiSuggestions;
    const aiAnalyses = planConfig.aiAnalyses;

    return (
      <div className="fixed top-0 left-0 right-0 z-[1200] bg-amber-500 text-white text-center py-1.5 text-xs font-medium">
        <div className="flex items-center justify-center gap-3 flex-wrap px-4">
          <span className="flex items-center gap-1.5">
            <Eye size={12} />
            <strong>{t('impersonate.viewingAs', { role: viewAsRole })}</strong>
          </span>
          <span className="hidden sm:flex items-center gap-2 text-amber-100">
            |
            {!hasExport && <span>⛔ {t('impersonate.noExport')}</span>}
            {!hasAnalytics && <span>⛔ {t('impersonate.noAnalytics')}</span>}
            {!hasCanvas && <span>⛔ {t('impersonate.noCanvas')}</span>}
            {!hasNoAds && <span>📢 {t('impersonate.adsVisible')}</span>}
            {hasAgent && <span>✅ {t('impersonate.agentAccess')}</span>}
            <span>🤖 AI: {aiSuggestions}/{aiAnalyses}</span>
          </span>
          <span className="text-amber-100">|</span>
          <span className="text-amber-100">{t('impersonate.dataFromAdmin')}</span>
          <button onClick={resetToAdmin} className="underline hover:text-amber-100 font-semibold">
            {t('impersonate.exit')}
          </button>
        </div>
      </div>
    );
  })();

  // Chromeless flows render with no navbar at all (must sit after all hooks).
  if (isChromeless) return null;

  /* ════════════════════════════════════════════
     MANGA NAVBAR
  ════════════════════════════════════════════ */
  if (isManga) {
    return (
      <>
      {impersonationBanner}
      <nav
        className="fixed top-0 left-0 right-0 z-[1100]"
        style={{ background: '#0a0a0a', borderBottom: '4px solid #E8192C', ...(isImpersonating ? { marginTop: 28 } : {}) }}
      >
        {/* Desktop bar */}
        <div style={{ display: 'flex', alignItems: 'center', height: 52, padding: '0 16px', gap: 10 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', minWidth: 0, overflow: 'hidden' }}>
            <div style={{
              border: '2px solid #E8192C', width: 28, height: 28,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Noto Serif JP', serif", fontSize: 14, fontWeight: 700,
              color: '#E8192C', flexShrink: 0,
            }}>旅</div>
            <span
              className="hidden sm:inline"
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 12, fontWeight: 900, color: '#fff',
                letterSpacing: '0.06em', textTransform: 'uppercase' as const,
              }}
            >TRIPGENESYS JAPAN</span>
          </Link>

          {/* Nav links */}
          <div className="hidden lg:flex" style={{ marginLeft: 8 }}>
            {allNavItems.map((item, i) => (
              <Link
                key={item.to}
                to={item.to}
                data-tour={navTourId(item.to)}
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 10, fontWeight: 900,
                  letterSpacing: '0.12em', textTransform: 'uppercase' as const,
                  color: isActive(item.to) ? '#fff' : 'rgba(255,255,255,0.35)',
                  padding: '0 10px', height: 52,
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
                  borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.12)' : 'none',
                  borderBottom: isActive(item.to) ? '3px solid #E8192C' : '3px solid transparent',
                  textDecoration: 'none',
                }}
              >
                <span style={{ lineHeight: 1 }}>{t(item.labelKey)}</span>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: isActive(item.to) ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.2)', marginTop: 3, textTransform: 'uppercase' as const }}>{t(item.subtitleKey)}</span>
              </Link>
            ))}
          </div>

          {/* Right cluster */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <div
              className="hidden sm:block"
              style={{
                background: '#E8192C', color: '#fff',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 9, fontWeight: 900, letterSpacing: '0.1em',
                padding: '2px 7px',
              }}
            >冒険 BŌKEN</div>

            {!betaGuestMode && (
              <button onClick={search.open} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4 }} aria-label="Search">
                <Search size={15} />
              </button>
            )}
            {isMangaV3 ? (
              <button onClick={toggleMangaBW} style={{ background: 'none', border: 'none', color: isMangaBW ? '#E8192C' : 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4 }} aria-label={isMangaBW ? 'Switch to colour' : 'Switch to B&W'}>
                <Palette size={16} />
              </button>
            ) : (
              <button onClick={toggleTheme} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4 }} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            )}

            {user && creditBalance !== null && gamificationEnabled && (
              <div className="hidden md:block">
                <CreditsBanner balance={creditBalance} compact pulse={!hasCheckedInToday} onClick={() => { if (!hasCheckedInToday) { showCheckInIfNeeded(); } else { navigate('/credits'); } }} />
              </div>
            )}
            {user && <div className="hidden md:block"><NotificationCenter /></div>}

            {user ? (
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  data-tour="nav-credits"
                  style={{ background: 'none', border: '2px solid #fff', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                >
                  <UserAvatar size="sm" activeFrame={activeFrame} />
                </button>
                {userMenuDropdown}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to={betaGuestMode ? '/beta-signin' : '/signin'}><Button variant="ghost" size="sm" className="text-gray-100">{t('nav.signIn')}</Button></Link>
                {!betaGuestMode && (
                  <Link to="/signup"><Button variant="secondary" size="sm">{t('nav.signUp')}</Button></Link>
                )}
              </div>
            )}

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="overflow-y-auto overscroll-contain"
            style={{
              background: '#111',
              borderTop: '2px solid #E8192C',
              maxHeight: 'calc(100dvh - 64px)',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div style={{ padding: '8px 0 32px 0' }}>
              {allNavItems.map((item) => (
                <Link key={item.to} to={item.to} data-tour={navTourId(item.to)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 11, fontWeight: 900, letterSpacing: '0.1em',
                  textTransform: 'uppercase' as const,
                  color: isActive(item.to) ? '#fff' : 'rgba(255,255,255,0.45)',
                  textDecoration: 'none',
                  borderLeft: isActive(item.to) ? '3px solid #E8192C' : '3px solid transparent',
                }}>
                  <item.icon size={16} />{t(item.labelKey)}
                </Link>
              ))}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', marginTop: 4, paddingTop: 4 }}>
                {user ? (
                  <>
                  <button onClick={() => { search.open(); setIsMobileMenuOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'none', border: 'none', color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' as const, cursor: 'pointer', width: '100%' }}>
                    <Search size={16} />{t('common.search')}
                  </button>
                  <Link to="/help" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', textDecoration: 'none', color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' as const }}>
                    <HelpCircle size={16} />{t('nav.helpGuide')}
                  </Link>
                  {showTaxFreeNav && (
                    <Link to="/tax-free" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', textDecoration: 'none', color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' as const }}>
                      <Receipt size={16} />
                      {t('nav.taxFree')}
                      <span style={{ marginLeft: 4, padding: '1px 5px', fontSize: 8, fontWeight: 800, backgroundColor: '#dc2626', color: '#ffffff', borderRadius: 4, letterSpacing: '0.08em' }}>NEW</span>
                    </Link>
                  )}
                  <Link to="/subscription" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', textDecoration: 'none', color: '#fff', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', textTransform: 'uppercase' as const }}>
                    <Crown size={16} />{t('nav.myPlan')}
                  </Link>
                  <button onClick={signOut} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'none', border: 'none', color: '#E8192C', fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 900, letterSpacing: '0.1em', cursor: 'pointer', textTransform: 'uppercase' as const }}>
                    <LogOut size={16} />{t('nav.signOut')}
                  </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', gap: 8, padding: '8px 16px' }}>
                    <Link to={betaGuestMode ? '/beta-signin' : '/signin'} style={{ flex: 1 }}><Button variant="ghost" fullWidth>{t('nav.signIn')}</Button></Link>
                    {!betaGuestMode && (
                      <Link to="/signup" style={{ flex: 1 }}><Button variant="secondary" fullWidth>{t('nav.signUp')}</Button></Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
      </>
    );
  }

  /* ════════════════════════════════════════════
     UKIYO-E NAVBAR
  ════════════════════════════════════════════ */
  if (isUkiyo) {
    return (
      <>
      {impersonationBanner}
      <nav
        className="fixed top-0 left-0 right-0 z-[1100]"
        style={{ background: '#0D3A6B', borderBottom: '3px solid #B8860B', ...(isImpersonating ? { marginTop: 28 } : {}) }}
      >
        {/* Desktop bar */}
        <div style={{ display: 'flex', alignItems: 'center', height: 52, padding: '0 16px', gap: 10 }}>

          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', minWidth: 0, overflow: 'hidden' }}>
            <div style={{
              background: 'rgba(212,160,23,0.18)', border: '1px solid #B8860B',
              width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Noto Serif JP', serif", fontSize: 14, color: '#B8860B', flexShrink: 0,
            }}>旅</div>
            <span
              className="hidden sm:inline"
              style={{
                fontFamily: "'Noto Serif JP', serif",
                fontSize: 13, fontWeight: 600, color: '#fff', letterSpacing: '0.04em',
              }}
            >TripGenesys Japan</span>
          </Link>

          {/* Nav links */}
          <div className="hidden lg:flex" style={{ marginLeft: 8 }}>
            {allNavItems.map((item, i) => (
              <Link
                key={item.to}
                to={item.to}
                data-tour={navTourId(item.to)}
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontSize: 12, fontWeight: isActive(item.to) ? 600 : 400,
                  color: isActive(item.to) ? '#fff' : 'rgba(255,255,255,0.45)',
                  padding: '0 12px', height: 52,
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
                  borderLeft: i > 0 ? '1px solid rgba(184,134,11,0.35)' : 'none',
                  borderBottom: isActive(item.to) ? '2px solid #B8860B' : '2px solid transparent',
                  textDecoration: 'none',
                }}
              >
                <span style={{ lineHeight: 1 }}>{t(item.labelKey)}</span>
                <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 9, fontWeight: 600, letterSpacing: '0.1em', color: isActive(item.to) ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.28)', marginTop: 3 }}>{t(item.subtitleKey)}</span>
              </Link>
            ))}
          </div>

          {/* Right cluster */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {!betaGuestMode && (
              <button onClick={search.open} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4 }} aria-label="Search">
                <Search size={16} />
              </button>
            )}
            {isMangaV3 ? (
              <button onClick={toggleMangaBW} style={{ background: 'none', border: 'none', color: isMangaBW ? '#E8192C' : 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4 }} aria-label={isMangaBW ? 'Switch to colour' : 'Switch to B&W'}>
                <Palette size={16} />
              </button>
            ) : (
              <button onClick={toggleTheme} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', padding: 4 }} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            )}
            <LanguageSelector variant="compact" buttonClassName={isV2Active ? `${v2NavText} hover:opacity-80` : undefined} />

            {user && creditBalance !== null && gamificationEnabled && (
              <div className="hidden md:block">
                <CreditsBanner balance={creditBalance} compact pulse={!hasCheckedInToday} onClick={() => { if (!hasCheckedInToday) { showCheckInIfNeeded(); } else { navigate('/credits'); } }} />
              </div>
            )}
            {user && <div className="hidden md:block"><NotificationCenter /></div>}

            {user ? (
              <div className="relative hidden md:block" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  data-tour="nav-credits"
                  style={{ background: 'rgba(13,58,107,0.6)', border: '2px solid #B8860B', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                >
                  <UserAvatar size="sm" activeFrame={activeFrame} />
                </button>
                {userMenuDropdown}
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to={betaGuestMode ? '/beta-signin' : '/signin'}><Button variant="ghost" size="sm" className="text-gray-100">{t('nav.signIn')}</Button></Link>
                {!betaGuestMode && (
                  <Link to="/signup"><Button variant="secondary" size="sm">{t('nav.signUp')}</Button></Link>
                )}
              </div>
            )}

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden" style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 4, flexShrink: 0 }}>
              {isMobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div
            className="overflow-y-auto overscroll-contain"
            style={{
              background: '#0D2E52',
              borderTop: '1px solid rgba(184,134,11,0.4)',
              maxHeight: 'calc(100dvh - 64px)',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <div style={{ padding: '8px 0 32px 0' }}>
              {allNavItems.map((item) => (
                <Link key={item.to} to={item.to} data-tour={navTourId(item.to)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                  fontFamily: "'Noto Serif JP', serif",
                  fontSize: 13, fontWeight: isActive(item.to) ? 600 : 400,
                  color: isActive(item.to) ? '#fff' : 'rgba(255,255,255,0.5)',
                  textDecoration: 'none',
                  borderLeft: isActive(item.to) ? '2px solid #B8860B' : '2px solid transparent',
                }}>
                  <item.icon size={16} />{t(item.labelKey)}
                </Link>
              ))}
              <div style={{ borderTop: '1px solid rgba(184,134,11,0.25)', marginTop: 4, paddingTop: 4 }}>
                {user ? (
                  <>
                  <button onClick={() => { search.open(); setIsMobileMenuOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'none', border: 'none', color: '#1B5C8A', fontFamily: "'Noto Serif JP', serif", fontSize: 13, cursor: 'pointer', width: '100%' }}>
                    <Search size={16} />{t('common.search')}
                  </button>
                  <Link to="/help" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', textDecoration: 'none', color: '#1B5C8A', fontFamily: "'Noto Serif JP', serif", fontSize: 13 }}>
                    <HelpCircle size={16} />{t('nav.helpGuide')}
                  </Link>
                  {showTaxFreeNav && (
                    <Link to="/tax-free" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', textDecoration: 'none', color: '#1B5C8A', fontFamily: "'Noto Serif JP', serif", fontSize: 13 }}>
                      <Receipt size={16} />
                      {t('nav.taxFree')}
                      <span style={{ marginLeft: 4, padding: '1px 5px', fontSize: 8, fontWeight: 800, backgroundColor: '#B8860B', color: '#fff', borderRadius: 0, letterSpacing: '0.08em' }}>NEW</span>
                    </Link>
                  )}
                  <Link to="/subscription" onClick={() => setIsMobileMenuOpen(false)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', textDecoration: 'none', color: '#1B5C8A', fontFamily: "'Noto Serif JP', serif", fontSize: 13 }}>
                    <Crown size={16} />{t('nav.myPlan')}
                  </Link>
                  <button onClick={signOut} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'none', border: 'none', color: '#C0392B', fontFamily: "'Noto Serif JP', serif", fontSize: 13, cursor: 'pointer' }}>
                    <LogOut size={16} />{t('nav.signOut')}
                  </button>
                  </>
                ) : (
                  <div style={{ display: 'flex', gap: 8, padding: '8px 16px' }}>
                    <Link to={betaGuestMode ? '/beta-signin' : '/signin'} style={{ flex: 1 }}><Button variant="ghost" fullWidth>{t('nav.signIn')}</Button></Link>
                    {!betaGuestMode && (
                      <Link to="/signup" style={{ flex: 1 }}><Button variant="secondary" fullWidth>{t('nav.signUp')}</Button></Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
      </>
    );
  }

  /* ════════════════════════════════════════════
     STANDARD NAVBAR (light / dark)
  ════════════════════════════════════════════ */
  const isHomePage = location.pathname === '/';
  // V3 skips the v2-navbar-frost class so its !important cream rule doesn't
  // override the inline white background applied on <nav>.
  const navBg = isMangaV3
    ? (scrolled ? 'shadow-warm' : '')
    : isV2Manga
    ? 'v2-navbar-manga'
    : isV2Ukiyo
    ? 'v2-navbar-ukiyo' + (scrolled ? ' shadow-warm' : '')
    : isV2Standard
    ? 'v2-navbar-frost' + (scrolled ? ' shadow-warm border-b border-[rgb(232,226,214)]/80' : '')
    : scrolled
    ? 'bg-gray-900/95 backdrop-blur-lg shadow-warm border-b border-gray-800/50'
    : isHomePage
    ? 'bg-gray-900/80 backdrop-blur-sm'
    : 'bg-gray-900/90 backdrop-blur-md';

  // V2 themes other than Manga have light backgrounds in light mode, so the
  // existing text-gray-100 (white) becomes invisible. Swap for warm-dark in
  // light mode, light in dark mode. Manga keeps white text on the ink bar.
  const v2NavText = isV2Standard || isV2Ukiyo
    ? 'text-gray-900 dark:text-gray-100'
    : 'text-gray-100';

  return (
    <>
    {impersonationBanner}
    <nav className={`fixed top-0 left-0 right-0 z-[1100] transition-all duration-300 ${navBg}`} style={{
      ...(isImpersonating ? { marginTop: 28 } : null),
      ...(isMangaV3 ? { background: '#fff', borderBottom: '3px solid #0a0a0a', backdropFilter: 'none' } : null),
    }}>
      <div className="px-4 lg:px-6">
        <div className="flex items-center h-16">

          {/* Logo */}
          <Link to="/" className={`flex items-center space-x-2 ${isV2Active ? v2NavText : 'text-gray-100'} shrink-0 mr-4 min-w-0 overflow-hidden`}>
            <span
              className="flex items-center justify-center shrink-0"
              style={{
                width: 32, height: 32, fontSize: 16, fontWeight: 700,
                fontFamily: "'Noto Serif JP', serif", lineHeight: 1, color: '#fff',
                background: isV2Manga ? '#E8192C'
                  : isV2Ukiyo ? '#C0392B'
                  : isV2Standard ? 'rgb(160 120 80)'
                  : undefined,
                borderRadius: isV2Manga ? 0 : (isV2Ukiyo ? 2 : 8),
                border: isMangaV3 ? '2px solid #0a0a0a' : isV2Manga ? '2px solid #fff' : undefined,
              }}
            >
              旅
            </span>
            <span
              className="hidden sm:inline text-xl font-bold tracking-tight"
              style={
                isMangaV3 ? { fontFamily: "'Bangers', 'Impact', cursive", letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400, color: '#0a0a0a' }
                : isV2Standard ? { fontFamily: "'Fraunces', Georgia, serif", fontVariationSettings: "'opsz' 144, 'SOFT' 40", fontWeight: 500 }
                : isV2Manga ? { fontFamily: "'Bangers', 'Impact', cursive", letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 400 }
                : isV2Ukiyo ? { fontFamily: "'Shippori Mincho', 'Noto Serif JP', serif", fontWeight: 700, letterSpacing: '-0.01em', color: undefined }
                : undefined
              }
            >
              TripGenesys Japan
            </span>
          </Link>

          {/* Nav links with subtitles — sit immediately right of logo, NOT centred */}
          <div className="hidden lg:flex items-stretch h-16" data-tutorial="navbar-items">
            {allNavItems.map((item) => {
              const active = isActive(item.to);
              const v2ActiveBorder = isV2Manga ? '#E8192C' : isV2Ukiyo ? '#B8860B' : isV2Standard ? 'rgb(160 120 80)' : undefined;
              return (
              <div key={item.to} className="relative">
                <Link
                  to={item.to}
                  data-tour={navTourId(item.to)}
                  className={`flex flex-col justify-center px-4 h-16 border-b-2 transition-all duration-200 ${
                    isV2Active
                      ? (active
                          ? `${v2NavText}`
                          : `border-transparent ${isMangaV3 ? 'text-gray-700 hover:text-[#0a0a0a]' : isV2Manga ? 'text-gray-300 hover:text-white' : isV2Ukiyo ? 'text-[rgba(13,46,82,0.7)] dark:text-[rgba(232,223,200,0.7)] hover:text-[#0D2E52] dark:hover:text-[#E8DFC8]' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}`)
                      : (active
                          ? 'border-secondary-400 text-white'
                          : 'border-transparent text-gray-300 hover:text-white hover:border-gray-500')
                  }`}
                  style={isV2Active && active ? { borderBottomColor: v2ActiveBorder } : undefined}
                >
                  <span className="text-sm font-semibold leading-tight">{t(item.labelKey)}</span>
                  <span className="text-[11px] text-gray-400 leading-tight mt-0.5">{t(item.subtitleKey)}</span>
                </Link>
                {item.to === '/admin' && adminErrorCount > 0 && (
                  <span className="absolute top-2 right-0 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center" title={`${adminErrorCount} error${adminErrorCount !== 1 ? 's' : ''} in last 24h`}>
                    {adminErrorCount > 9 ? '9+' : adminErrorCount}
                  </span>
                )}
              </div>
              );
            })}
          </div>

          {/* Spacer — pushes right cluster to far right */}
          <div className="flex-1" />

          {/* Right cluster */}
          <div className="flex items-center space-x-2 shrink-0">
            {!betaGuestMode && (
              <button onClick={search.open} className={`p-2 rounded-xl transition-all duration-200 ${isV2Active ? v2NavText : 'text-gray-100'} ${isV2Standard || isV2Ukiyo ? 'hover:bg-black/5 dark:hover:bg-white/10' : 'hover:bg-white/10'}`} aria-label="Search" data-tutorial="navbar-search">
                <Search size={20} />
              </button>
            )}
            {isMangaV3 ? (
              <button onClick={toggleMangaBW} className={`p-2 rounded-xl transition-all duration-200 ${isMangaBW ? 'text-red-500' : isV2Active ? v2NavText : 'text-gray-100'} ${isV2Standard || isV2Ukiyo ? 'hover:bg-black/5 dark:hover:bg-white/10' : 'hover:bg-white/10'}`} aria-label={isMangaBW ? 'Switch to colour' : 'Switch to B&W'} data-tutorial="navbar-theme">
                <Palette size={20} />
              </button>
            ) : (
              <button onClick={toggleTheme} className={`p-2 rounded-xl transition-all duration-200 ${isV2Active ? v2NavText : 'text-gray-100'} ${isV2Standard || isV2Ukiyo ? 'hover:bg-black/5 dark:hover:bg-white/10' : 'hover:bg-white/10'}`} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} data-tutorial="navbar-theme">
                {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
              </button>
            )}
            <LanguageSelector variant="compact" buttonClassName={isV2Active ? `${v2NavText} hover:opacity-80` : undefined} />

            {user && creditBalance !== null && gamificationEnabled && (
              <div className="hidden md:block">
                <CreditsBanner balance={creditBalance} compact pulse={!hasCheckedInToday} onClick={() => { if (!hasCheckedInToday) { showCheckInIfNeeded(); } else { navigate('/credits'); } }} />
              </div>
            )}
            {user && <div className="hidden md:block"><NotificationCenter /></div>}

            {user ? (
              <div className="relative hidden md:block" ref={userMenuRef} data-tutorial="navbar-user">
                <button onClick={() => setShowUserMenu(!showUserMenu)} data-tour="nav-credits" className={`flex items-center gap-2 p-2 rounded-xl transition-all duration-200 ${isV2Active ? v2NavText : 'text-gray-100'} ${isV2Standard || isV2Ukiyo ? 'hover:bg-black/5 dark:hover:bg-white/10' : 'hover:bg-white/10'}`}>
                  <UserAvatar size="sm" activeFrame={activeFrame} />
                  {activeTitle && <TitleBadge slug={activeTitle} size="sm" />}
                </button>
                {userMenuDropdown}
              </div>
            ) : (
              <div className="hidden md:flex items-center space-x-2">
                <Link to={betaGuestMode ? '/beta-signin' : '/signin'}><Button variant="ghost" size="sm" className={isV2Active ? v2NavText : 'text-gray-100'}>{t('nav.signIn')}</Button></Link>
                {!betaGuestMode && (
                  <Link to="/signup"><Button variant="secondary" size="sm">{t('nav.signUp')}</Button></Link>
                )}
              </div>
            )}

            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className={`lg:hidden p-2 rounded-xl transition-colors ${isV2Active ? v2NavText : 'text-gray-100'} ${isV2Standard || isV2Ukiyo ? 'hover:bg-black/5 dark:hover:bg-white/10' : 'hover:bg-white/10'} shrink-0`} aria-label="Toggle mobile menu">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu — standard mode */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-warm-lg animate-slide-down overflow-y-auto overscroll-contain"
          style={{
            maxHeight: 'calc(100dvh - 64px)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div className="container mx-auto px-4 pt-4 pb-8 space-y-1">
            {allNavItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-tour={navTourId(item.to)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? 'bg-secondary-50 dark:bg-secondary-900/20 text-secondary-600 dark:text-secondary-400'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <item.icon size={18} />
                <div>
                  <div>{t(item.labelKey)}</div>
                  <div className="text-xs text-gray-400 dark:text-gray-500">{t(item.subtitleKey)}</div>
                </div>
              </Link>
            ))}
            <div className="pt-3 border-t border-gray-200 dark:border-gray-800">
              {user ? (
                <>
                  {creditBalance !== null && gamificationEnabled && (
                    <Link to="/credits" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <Zap size={18} className="text-amber-500" /><span>{formatNumber(creditBalance)} Credits</span>
                    </Link>
                  )}
                  <Link to="/profile" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <User size={18} /><span>{t('nav.profile')}</span>
                  </Link>
                  {user && !isAgent && (
                    <div className="px-4 py-2">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-1.5 flex items-center gap-1">
                        <Sparkles size={12} /> {t('nav.travelMode')}
                      </p>
                      <div role="radiogroup" aria-label={t('nav.travelMode')} className="flex p-0.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                        <button
                          role="radio"
                          aria-checked={isCasual}
                          onClick={() => { if (!isCasual) toggleTravelMode(); }}
                          className={`flex-1 px-3 py-2 rounded-md transition-all ${
                            isCasual
                              ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {t('nav.casual')}
                        </button>
                        <button
                          role="radio"
                          aria-checked={!isCasual}
                          onClick={() => { if (isCasual) toggleTravelMode(); }}
                          className={`flex-1 px-3 py-2 rounded-md transition-all ${
                            !isCasual
                              ? 'bg-white dark:bg-gray-900 text-primary-600 dark:text-primary-400 shadow-sm'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}
                        >
                          {t('nav.enthusiast')}
                        </button>
                      </div>
                    </div>
                  )}
                  {gamificationEnabled && (
                    <>
                      <Link to="/achievements" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
                        <Trophy size={18} /><span>{t('nav.achievements')}</span>
                      </Link>
                    </>
                  )}
                  {!betaGuestMode && (
                    <button onClick={() => { search.open(); setIsMobileMenuOpen(false); }} className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 w-full">
                      <Search size={18} /><span>{t('common.search')}</span>
                    </button>
                  )}
                  <Link to="/help" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => setIsMobileMenuOpen(false)}>
                    <HelpCircle size={18} /><span>{t('nav.helpGuide')}</span>
                  </Link>
                  {showTaxFreeNav && (
                    <Link to="/tax-free" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => setIsMobileMenuOpen(false)}>
                      <Receipt size={18} /><span>{t('nav.taxFree')}</span>
                      <span
                        className="ml-auto px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider rounded"
                        style={{ backgroundColor: '#dc2626', color: '#ffffff' }}
                      >
                        {t('common:common.new')}
                      </span>
                    </Link>
                  )}
                  <Link to="/subscription" className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800" onClick={() => setIsMobileMenuOpen(false)}>
                    <Crown size={18} /><span>{t('nav.myPlan')}</span>
                  </Link>
                  <button onClick={signOut} className="flex items-center space-x-3 px-4 py-3 rounded-xl text-sm text-error-600 dark:text-error-400 w-full hover:bg-error-50 dark:hover:bg-error-900/20">
                    <LogOut size={18} /><span>{t('nav.signOut')}</span>
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-4 py-2">
                  <Link to={betaGuestMode ? '/beta-signin' : '/signin'} className="flex-1"><Button variant="outline" fullWidth>{t('nav.signIn')}</Button></Link>
                  {!betaGuestMode && (
                    <Link to="/signup" className="flex-1"><Button variant="secondary" fullWidth>{t('nav.signUp')}</Button></Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
    </>
  );
}
